import { ProductCardBuilder } from '../../components/ProductCard/ProductCardBuilder';
import { PromoTileBuilder } from '../../components/PromoTile/PromoTileBuilder';
import { isVideo } from '../../utils/video';
import { Dropdown } from '../../components/Dropdown';
import { Modal } from '../../components/Modal';
import { Filter } from './Filter';
import { MobileSort } from './MobileSort';
import interactionBlocker from './interactionBlocker';
import { trackSearchResultsLoaded, trackBrowseResultsLoaded, mapResultsToItems } from '../../utils/ConstructorTracking';
import {
  viewSearchResultsTracking,
  impressionsTracking,
  sortSelectionTracking,
  filterClickTracking,
} from '../../components/GTMEvents';
import { cnstrcSdk, constructorParamsBuilder } from '../../core/ConstructorSdk';
import CategoryPill from '../../components/CategoryPill';

// Facets that get their own top-level URL param (comma-separated)
const DIRECT_PARAM_FACETS = { color_family: 'color', size: 'size' };

// Reverse map: URL param name → Constructor facet name
const PARAM_TO_FACET = { color: 'color_family', size: 'size' };

// Build sort maps from the DOM — Liquid is the single source of truth
const SORT_URL_TO_API = {};
const SORT_API_TO_URL = {};

document.querySelectorAll('#desktop-sort-by-options li[data-sort-name]').forEach((li) => {
  const sortBy = li.dataset.value;
  const sortOrder = li.dataset.order;
  const urlName = li.dataset.sortName;
  SORT_URL_TO_API[urlName] = { sortBy, sortOrder };
  SORT_API_TO_URL[`${sortBy}:${sortOrder}`] = urlName;
});

class PLP {
  #searchResultsTracked = false;
  #impressionObserver = null;
  #entrySource = null;
  #pivotPromoTile = false;

  constructor() {
    this.numberOfResultsPerPage = 48;
    this.page = 1;
    this.pod =
      document.querySelector('.plp-c-results__list[data-cnstrc-browse]') ||
      document.querySelector('.plp-c-results__list[data-cnstrc-search]');
    this.isBrowse = false;
    this.isSearch = false;
    this.sidebar = new Filter('plpFilterSidebar', 'myOverlay');
    this.pagination = document.querySelector('.plp-c-pagination__wrapper');
    this.date = Math.floor(Date.now() / 1000); // Get seconds

    this.gridSwitchBtns = document.querySelectorAll('.plp-c-results-header__grid-pod button');
    // Restore sort from URL or default to relevance
    const urlParams = new URLSearchParams(window.location.search);
    const urlSort = SORT_URL_TO_API[urlParams.get('sortBy')] || SORT_URL_TO_API.relevency;
    this.sortBy = urlSort?.sortBy;
    this.sortOrder = urlSort?.sortOrder;
    this.desktopSortId = 'desktop-sort-by-wrapper';
    this.mobileSortId = 'plp-mobile-sort-modal';
    this.activeFilters = {};
    this.collectionId = this.pod?.dataset.collectionId || null;
    this.fallbackSearchResultsPodId =
      document.querySelector('[data-fallback-search-results]')?.dataset.fallbackSearchPod || '';

    if (this.pod?.hasAttribute('data-cnstrc-browse')) {
      this.isBrowse = true;
    } else if (this.pod?.hasAttribute('data-cnstrc-search')) {
      this.isSearch = true;
    }

    // Restore page and filters from URL on page load
    this.page = parseInt(urlParams.get('page')) || 1;
    this.urlFilters = this.#parseFiltersFromUrl();
    // Merge price ranges for API calls
    this.activeFilters = { ...this.urlFilters };
    this.#mergeFiltersForApi(this.activeFilters);
  }

  init() {
    if (!this.pod) return;

    this.#updateUrlAddress(this.page > 1 ? this.page : null, true);
    // The SEO product grid is removed from the DOM.
    this.#cleanCachedCollection();

    this.#call()
      .then(this.#checkURLRedirect.bind(this))
      .then(this.#populate.bind(this))
      .catch((error) => {
        this.#errorHandler(error);
        this.toggleFilters({ showFilters: false, showSort: false });
        this.#fetchAndRenderFallbackRecommendations();
        this.#callRecommendations(0, () => {
          window.recommendations?.enqueue();
        });
      });

    // Defer this block of code because they aren't user-visible tasks.
    setTimeout(() => {
      this.#captureEntrySource();
      this.#bindEvents();
      this.#bindSortEvents();
      window.innerWidth <= 990 && this.#bindModalEvents();
      this.#restoreSortFromUrl();
      this.#bindSidebarEvents();
      this.#bindPopstate();
      new CategoryPill().init(); // This is technically a component.
    }, 0);
  }

  #checkURLRedirect(data) {
    const redirectUrl = data?.response?.redirect?.data?.url;

    if (redirectUrl) {
      window.location.href = redirectUrl;
      return new Promise(() => {});
    }

    return data;
  }

  /**
   * Capture the `entry` query param (e.g. ?entry=cta) and clean it from the URL.
   */
  #captureEntrySource() {
    const initParams = new URLSearchParams(window.location.search);
    this.#entrySource = initParams.get('entry') || 'default';
    if (initParams.has('entry')) {
      initParams.delete('entry');
      const cleanUrl = initParams.toString()
        ? `${window.location.pathname}?${initParams.toString()}`
        : window.location.pathname;
      window.history.replaceState(null, '', cleanUrl);
    }
  }

  /**
   * Removes the interaction blocker overlay after a Constructor API call completes.
   * Deferred to the next animation frame to ensure the DOM has finished updating.
   */
  #endCall() {
    requestAnimationFrame(() => interactionBlocker());
  }

  /**
   * Parse filter facets from the current URL query string.
   * @returns {Object} filters object e.g. { color_family: ['Black','White'], size: ['S'] }
   */
  #parseFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const filters = {};

    // Direct params: color → color_family, size → size
    for (const [paramName, facetName] of Object.entries(PARAM_TO_FACET)) {
      const value = params.get(paramName);
      if (value) filters[facetName] = value.split(',');
    }

    // JSON filters param: [["priceUS","10-50"],["neckline","V-Neck"]]
    const filtersParam = params.get('filters');
    if (filtersParam) {
      try {
        const parsed = JSON.parse(filtersParam);
        if (Array.isArray(parsed)) {
          parsed.forEach(([facetName, value]) => {
            if (!facetName || !value) return;
            if (!filters[facetName]) filters[facetName] = [];
            if (!filters[facetName].includes(value)) filters[facetName].push(value);
          });
        }
      } catch (e) {
        console.error('Error parsing filters URL param:', e);
      }
    }

    const country = window.Shopify?.country || 'US';
    for (const name of Object.keys(filters)) {
      if (name.startsWith('price') && name !== `price${country}`) {
        delete filters[name];
      }
    }

    return filters;
  }

  /**
   * Merge contiguous price ranges into a single range for the API call.
   * @param {Object} filters - filters object (will be mutated)
   */
  #mergeFiltersForApi(filters) {
    for (const name of Object.keys(filters)) {
      if (!name.startsWith('price') || filters[name].length <= 1) continue;

      let rangeMin = Infinity;
      let rangeMax = -Infinity;
      filters[name].forEach((value) => {
        const [lo, hi] = value.split('-').map(Number);
        if (lo < rangeMin) rangeMin = lo;
        if (hi > rangeMax) rangeMax = hi;
      });

      filters[name] = [`${rangeMin}-${rangeMax}`];
    }
  }

  /**
   * Serialize active filters + page into the URL and push a history entry.
   * @param {null | Number} page - current page number (null removes the param)
   * @param {boolean} [replace=false] - use replaceState instead of pushState
   */
  #updateUrlAddress(page = null, replace = false) {
    const params = new URLSearchParams(window.location.search);
    const urlFilters = this.urlFilters || this.activeFilters;

    // Clean previous filter/sort/page params
    params.delete('color');
    params.delete('size');
    params.delete('filters');
    params.delete('sortBy');
    params.delete('page');

    // Update page param (omit for page 1)
    if (page !== null && page > 1) {
      params.set('page', page);
    }
    this.page = page !== null ? parseInt(page) || 1 : 1;

    // Build filter params using individual selections (not merged ranges)
    const jsonFilters = [];

    for (const [facet, values] of Object.entries(urlFilters)) {
      if (!values.length) continue;

      const directParam = DIRECT_PARAM_FACETS[facet];
      if (directParam) {
        params.set(directParam, values.join(','));
      } else {
        values.forEach((value) => jsonFilters.push([facet, value]));
      }
    }

    if (jsonFilters.length) {
      params.set('filters', JSON.stringify(jsonFilters));
    }

    // Add sort to URL (omit default unless user explicitly selected it)
    const sortUrlValue = SORT_API_TO_URL[`${this.sortBy}:${this.sortOrder}`];
    if (sortUrlValue && (sortUrlValue !== 'relevency' || this.userChangedSort)) {
      params.set('sortBy', sortUrlValue);
    }

    // Build a consistent query string with proper encoding (especially for search term) and push/replace history entry
    let queryString = '';
    let count = 0;

    for (const [key, value] of params.entries()) {
      queryString += `${key}=${key === 'q' ? encodeURIComponent(value) : value}`;

      count++;
      if (count < params.size) {
        queryString += '&';
      }
    }

    const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
    if (replace) {
      window.history.replaceState({ filters: urlFilters, page: this.page }, '', newUrl);
    } else {
      window.history.pushState({ filters: urlFilters, page: this.page }, '', newUrl);
    }
    this.#updateCanonical();
  }

  /**
   * Update the canonical <link> tag to reflect the current page.
   * Includes ?page=N for pages > 1 (each page has unique content).
   * Excludes filters and sorting to avoid duplicate content issues.
   */
  #updateCanonical() {
    const canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) return;
    const page = this.page;
    canonical.href =
      page > 1
        ? `${window.location.origin}${window.location.pathname}?page=${page}`
        : `${window.location.origin}${window.location.pathname}`;
  }

  /**
   * Scroll the page to the top of the product grid.
   * When the sidebar is open, body has `position: fixed` and `overflow: hidden`,
   * which prevents `window.scrollTo` from working. In that case, we animate
   * `body.style.top` to 0 and reset the sidebar's saved scroll position so
   * closing the sidebar doesn't restore the old position.
   * @param {Object} options
   * @param {boolean} options.fixedBody - true when called from a context where
   *   the body is locked (e.g. filter sidebar open)
   */
  #scrollToTheTop({ fixedBody } = {}) {
    // If a mobile modal (sort/size/color/price/subcategories) is the active overlay,
    // record that its close should land at the top — we still animate via window.scrollTo below.
    const openModal = [
      this.mobileSort,
      this.mobileSizeModal,
      this.mobileColorModal,
      this.mobilePriceModal,
      this.subcategoriesModal,
    ].find((modal) => modal?.node?.open);
    if (openModal) openModal.scrollPosition = 0;

    // Only use the body-top animation when the body is actually locked
    // (sidebar's `body-no-scroll` class). For modal context, clear-filter
    // from the page, or any other unlocked state, fall through to the
    // standard window.scrollTo animation.
    const bodyLocked = document.body.classList.contains('body-no-scroll');

    if (fixedBody && bodyLocked) {
      this.sidebar.scrollPosition = 0;
      document.body.style.transition = 'top 0.2s ease';
      document.body.style.top = '0px';
      document.body.addEventListener(
        'transitionend',
        () => {
          document.body.style.transition = '';
        },
        { once: true },
      );
    } else {
      const start = window.scrollY;
      if (start === 0) return;
      const duration = 200;
      const startTime = performance.now();

      const step = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const ease = 1 - Math.pow(1 - progress, 3);
        window.scrollTo(0, start * (1 - ease));
        if (progress < 1) requestAnimationFrame(step);
      };

      requestAnimationFrame(step);
    }
  }

  /**
   * Attach event listeners on PLP HTML elements
   */
  #bindEvents() {
    this.gridSwitchBtns.forEach((btn) => btn.addEventListener('click', this.#gridSwitchHandle.bind(this)));
    this.pagination.querySelectorAll('a[role="button"]').forEach((btn) => {
      btn.addEventListener('click', this.#loadMoreResultsHandle.bind(this));
    });
  }

  #bindSortEvents() {
    this.sort = new Dropdown({
      wrapperId: this.desktopSortId,
      optionsId: 'desktop-sort-by-options',
      selectId: 'desktop-sort-by-select',
    });
    this.sort.init(this.#sortOptionHandle.bind(this));
  }

  #restoreSortFromUrl() {
    const match = document.querySelector(`li[data-value="${this.sortBy}"][data-order="${this.sortOrder}"]`);
    if (!match) return;

    // Update desktop dropdown display text and selected option
    const desktopSelect = document.getElementById('desktop-sort-by-select');
    if (desktopSelect) desktopSelect.textContent = match.textContent;

    const desktopOptions = document.querySelectorAll('#desktop-sort-by-options li[data-value]');
    desktopOptions.forEach((li) => li.classList.remove('plp-c-sort-by__selected-option'));
    const desktopMatch = document.querySelector(
      `#desktop-sort-by-options li[data-value="${this.sortBy}"][data-order="${this.sortOrder}"]`,
    );
    if (desktopMatch) desktopMatch.classList.add('plp-c-sort-by__selected-option');

    // Update mobile sort selected radio
    const mobileMatch = document.querySelector(
      `#${this.mobileSortId} input[data-value="${this.sortBy}"][data-order="${this.sortOrder}"]`,
    );
    if (mobileMatch) mobileMatch.checked = true;
  }

  #bindModalEvents() {
    // Sort modal
    this.mobileSort = new MobileSort({
      dialog: this.mobileSortId,
      form: document.getElementById(this.mobileSortId),
      context: { cleanContent: false },
    });
    document.getElementById('mobileOpenSort')?.addEventListener('click', () => this.mobileSort.show());
    this.mobileSort.onSortChange(this.#sortOptionHandle.bind(this));
    this.mobileSort.setClose(() => {});

    // Size filter modal
    this.mobileSizeModal = new Modal('plp-mobile-size-modal', { cleanContent: false });
    document.getElementById('mobileOpenSize')?.addEventListener('click', () => this.mobileSizeModal.show());
    this.mobileSizeModal.setClose(() => {});

    // Color filter modal
    this.mobileColorModal = new Modal('plp-mobile-color-modal', { cleanContent: false });
    document.getElementById('mobileOpenColor')?.addEventListener('click', () => this.mobileColorModal.show());
    this.mobileColorModal.setClose(() => {});

    // Price filter modal
    this.mobilePriceModal = new Modal('plp-mobile-price-modal', { cleanContent: false });
    document.getElementById('mobileOpenPrice')?.addEventListener('click', () => this.mobilePriceModal.show());
    this.mobilePriceModal.setClose(() => {});

    // Subcategories modal (collection pages only)
    const subcategoriesBtn = document.getElementById('subcategories-button');
    if (subcategoriesBtn) {
      this.subcategoriesModal = new Modal('plp-subcategories-modal', { cleanContent: false });
      subcategoriesBtn.addEventListener('click', () => this.subcategoriesModal.show());
      this.subcategoriesModal.setClose(() => {});
    }
  }

  #bindSidebarEvents() {
    this.sidebar.setClose();

    // Register mobile modal containers for individual facets
    const sizeModalContent = document.getElementById('plp-mobile-size-modal-content');
    if (sizeModalContent) this.sidebar.registerFacetContainer('size', sizeModalContent);

    const colorModalContent = document.getElementById('plp-mobile-color-modal-content');
    if (colorModalContent) this.sidebar.registerFacetContainer('color_family', colorModalContent);

    const country = window.Shopify?.country || 'US';
    const priceFacetName = `price${country}`;
    const priceModalContent = document.getElementById('plp-mobile-price-modal-content');
    if (priceModalContent) this.sidebar.registerFacetContainer(priceFacetName, priceModalContent);

    // Seed sidebar with individual selections restored from URL
    if (Object.keys(this.urlFilters).length) {
      this.sidebar.selectedFilters = { ...this.urlFilters };
    }

    this.sidebar.onFilterChange((apiFilters, uiFilters) => {
      this.activeFilters = { ...apiFilters };
      this.urlFilters = { ...uiFilters };
      this.page = 1;

      // Interaction blockers: block interaction during filter change
      interactionBlocker(true);
      this.#updateUrlAddress();
      this.#call()
        .then(this.#populate.bind(this))
        .catch((error) => this.#errorHandler(error))
        .finally(() => this.#endCall());

      this.#scrollToTheTop({ fixedBody: true });
    });

    // Open Filter sidebar
    const showSidebarButton = document.querySelectorAll('[data-show-sidebar]');
    showSidebarButton.forEach((button) =>
      button.addEventListener('click', () => {
        this.sidebar.show();
        // Defer no user-visible tasks
        setTimeout(() => {
          filterClickTracking();
        }, 0);
      }),
    );
  }

  /**
   * Listen for browser back/forward navigation and restore filter state
   */
  #bindPopstate() {
    window.addEventListener('popstate', () => {
      this.urlFilters = this.#parseFiltersFromUrl();
      this.activeFilters = { ...this.urlFilters };
      this.#mergeFiltersForApi(this.activeFilters);
      this.sidebar.selectedFilters = { ...this.urlFilters };

      const popParams = new URLSearchParams(window.location.search);
      const popSort = SORT_URL_TO_API[popParams.get('sortBy')] || SORT_URL_TO_API.relevency;
      this.sortBy = popSort.sortBy;
      this.sortOrder = popSort.sortOrder;
      this.#restoreSortFromUrl();

      this.#updateCanonical();

      interactionBlocker(true);
      this.#call()
        .then(this.#populate.bind(this))
        .catch((error) => this.#errorHandler(error))
        .finally(() => this.#endCall());
    });
  }

  /**
   * Set total of results on each HTML element
   * @param {Number} numResults - total of product results
   */
  updateTotalResults(numResults) {
    const resultsSummaryElementsTwo = document.querySelectorAll('[data-results-summary]');
    resultsSummaryElementsTwo.forEach((el) => (el.textContent = `${numResults} result${numResults === 1 ? '' : 's'}`));
  }

  /**
   * Click pagination buttons handler
   * @param {EventTarget} e - Click Pagination event to handle
   */
  #loadMoreResultsHandle(e) {
    e.stopPropagation();
    e.preventDefault();

    const btn = e.currentTarget;

    const currentPage = this.page;
    this.#updateUrlAddress(btn.dataset.right ? currentPage + 1 : currentPage - 1);

    // Interaction blockers: block interaction during pagination
    interactionBlocker(true);
    this.#call()
      .then(this.#populate.bind(this))
      .catch((error) => this.#errorHandler(error))
      .finally(() => this.#endCall());

    this.#scrollToTheTop();
  }

  getCurrentItemsCount(results, renderedCount) {
    const start = this.page === 1 ? 1 : (this.page - 1) * this.numberOfResultsPerPage + 1;
    const end =
      renderedCount != null
        ? Math.min(start + renderedCount - 1, results.response.total_num_results)
        : Math.min(this.page * this.numberOfResultsPerPage, results.response.total_num_results);
    return `${start} - ${end}`;
  }

  /**
   * Update pagination UI based on the number of results
   * @param {Object} results - The results object containing pagination information
   * @returns {void}
   */
  #updatePagination(results, { visibleTotal = null, renderedOnPage = null } = {}) {
    // Reveal pagination
    this.pagination.classList.remove('plp-c-pagination__wrapper--hidden');
    // Identify where pagination is
    const pages = this.pagination.querySelector('nav>ul');
    // Clean up existing event listeners on page items before re-rendering to avoid duplicates
    const pageItems = pages.querySelectorAll('li>a');
    pageItems.forEach((item) => item.removeEventListener('click', this.#goToPage.bind(this)));

    const viewingResults = this.pagination.querySelector('p');
    const totalForDisplay = visibleTotal ?? results.response.total_num_results;
    viewingResults.innerHTML = `Viewing <b>${this.getCurrentItemsCount(
      results,
      renderedOnPage,
    )}</b> of ${totalForDisplay} styles`;
    const totalPages = Math.ceil(results.response.total_num_results / this.numberOfResultsPerPage);

    // Clear existing page nodes
    pages.innerHTML = '';
    // Update left/right buttons state
    const btns = this.pagination.querySelectorAll('a[role="button"]');

    if (this.page === 1) {
      btns[0].setAttribute('disabled', 'true');
      btns[0].removeAttribute('href');
      btns[1].removeAttribute('disabled');
      this.page < totalPages && btns[1].setAttribute('href', this.#buildPageUrl(this.page + 1));
    } else if (this.page === totalPages) {
      btns[0].removeAttribute('disabled');
      btns[0].href = this.#buildPageUrl(this.page - 1);
      btns[1].setAttribute('disabled', 'true');
      btns[1].removeAttribute('href');
    } else
      btns.forEach((btn) => {
        btn.removeAttribute('disabled');
        if (btn.dataset.right) btn.href = this.#buildPageUrl(this.page + 1);
        else btn.href = this.#buildPageUrl(this.page - 1);
      });

    // Simple render when few pages (show all)
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        const li = document.createElement('li');
        li.appendChild(this.#buildPage(i, this.page));
        pages.appendChild(li);
      }
      return;
    }

    // Dynamic pagination with special windows for edges:
    // - First 4 pages: show 1..4, '...', last
    // - Last 4 pages: show 1, '...', total-3..total
    // - Middle pages: show 1, '...', current-1..current+1, '...', last
    const total = totalPages;
    const current = this.page;

    let start;
    let end;

    if (current <= 3) {
      start = 2;
      end = Math.min(4, total - 1);
    } else if (current >= total - 2) {
      start = Math.max(2, total - 3);
      end = total - 1;
    } else {
      start = current - 1;
      end = current + 1;
    }

    const tokens = [1];
    if (start > 2) tokens.push('...');
    for (let p = start; p <= end; p++) tokens.push(p);
    if (end < total - 1) tokens.push('...');
    tokens.push(total);

    tokens.forEach((t) => {
      const li = document.createElement('li');
      if (t === '...') {
        li.innerHTML = `<span class="plp-c-pagination__page" aria-hidden="true">...</span>`;
      } else {
        li.appendChild(this.#buildPage(t, current));
      }
      pages.appendChild(li);
    });
  }

  /**
   * Build URL for a given page number, preserving current path and origin
   * @param {number} page - page number to build URL for
   * @returns {string} URL for the given page number, preserving current path and origin
   */
  #buildPageUrl(page) {
    return `${window.location.origin}${window.location.pathname}?page=${page}`;
  }

  /**
   * Build an anchor HTML element with pagination information
   * @param {Number} page - page index
   * @param {Number} currentPage - current page index
   * @returns {HTMLAnchorElement} - page HTML link element
   */
  #buildPage(page, currentPage) {
    const anchor = document.createElement('a');
    anchor.href = this.#buildPageUrl(page);
    anchor.className = 'plp-c-pagination__page';
    anchor.textContent = page;
    anchor.setAttribute('aria-label', `Page ${page}`);
    if (page == currentPage) {
      anchor.setAttribute('aria-current', 'page');
    }
    anchor.addEventListener('click', this.#goToPage.bind(this));
    return anchor;
  }

  #goToPage(e) {
    e.stopPropagation();
    e.preventDefault();
    this.#updateUrlAddress(e.target.textContent);

    // Interaction blockers: block interaction during page navigation
    interactionBlocker(true);
    this.#call()
      .then(this.#populate.bind(this))
      .catch((error) => this.#errorHandler(error))
      .finally(() => this.#endCall());

    this.#scrollToTheTop();
  }

  /**
   * Click grid switch handle on results
   * @param {Event} e - Click HTML Button event
   */
  #gridSwitchHandle(e) {
    e.stopPropagation();

    if (!e.target) return;

    this.pod.classList.toggle('plp-c-results__list--one-grid');
    this.gridSwitchBtns.forEach((btn) => {
      btn.classList.toggle('plp-c-results-header__grid-btn--active');
      btn.setAttribute('aria-pressed', btn.classList.contains('plp-c-results-header__grid-btn--active'));
    });
  }

  #populate(response) {
    // Disconnect any existing impression observer before clearing results
    if (this.#impressionObserver) {
      this.#impressionObserver.disconnect();
      this.#impressionObserver = null;
    }

    // Extract metadata from API response for Constructor tracking
    const resultId = response?.result_id || '';
    const results = response?.response?.results || [];

    const facets = response?.response?.facets || [];
    // total_num_results may be at top level or nested in response
    const numResults = response?.total_num_results || response?.response?.total_num_results || results.length;

    // Browse and Full Search call exception, in cases customer introduced an invalid page number.
    if (!results.length && this.page > 1) {
      this.#updateUrlAddress(1, true);
      return window.location.reload();
    }

    // Browse call exception, in cases there are no results.
    // Additionally, it will cover the scenario where
    //  - Collection or search result has 1 or more products with no filters applied, but with filters applied 0 products match the current filter selections
    // Showing filters and sort element will allow customers to exit from this scenario, clear filters and see results.
    if (!results.length && this.page === 1 && this.isBrowse) {
      this.toggleFilters({
        showFilters: facets.length > 0,
        showSort: facets.length > 0,
        showResults: true,
      });
      facets.length > 0 && this.sidebar.renderFacets(facets);
      this.updateTotalResults(numResults);
      this.sidebar.updateResultCount(numResults);
      this.pagination.classList.add('plp-c-pagination__wrapper--hidden');
      this.#callRecommendations(numResults, () => {
        window.recommendations?.enqueue();
      });

      // Clear previous results now that new ones are ready
      this.pod.innerHTML = '';

      return console.warn('Constructor PLP Warning: there were no results');
    }

    if (!results.length && this.isSearch) {
      this.toggleFilters({ showFilters: false, showSort: false });
      this.#fetchAndRenderFallbackRecommendations();
      this.#callRecommendations(numResults, () => {
        window.recommendations?.enqueue();
      });

      return console.warn('Constructor PLP Search Warning: there were no results');
    } else if (this.isSearch) {
      const searchHeading = document.querySelector('#searchHeading [data-first-heading]');
      const searchResultsHeadings = JSON.parse(
        document.querySelector('script[data-search-state-results-headings]').textContent,
      );
      searchHeading && (searchHeading.textContent = searchResultsHeadings['results-heading']);
    }

    // After checking for no results, if there are results but all of them are promo tiles,
    // log a warning since this is likely a misconfiguration from Constructor end.
    const areAllValidProducts = results.some((r) => r?.variations?.length > 0);
    if (!areAllValidProducts) {
      console.warn('Constructor PLP Warning: all results were promo tiles');
    }

    // Clear previous results now that new ones are ready
    this.pod.innerHTML = '';

    const podDisplayName =
      response?.response?.groups?.find((group) => group.group_id === this.collectionId)?.display_name || '';

    this.#pivotPromoTile = false;
    let skippedPlaceholderCount = 0;

    results.forEach((product, idx) => {
      const isPromoTile =
        product.data?.promo_size || !Array.isArray(product.variations) || product.variations.length === 0;
      if (isPromoTile) {
        const didRender = this.#buildPromoTile(product, idx);
        if (!didRender) {
          skippedPlaceholderCount++;
        }
        return;
      }
      this.#buildProductCard(product, { podDisplayName });
    });

    const visibleTotal = Math.max(0, numResults - skippedPlaceholderCount);
    const renderedOnPage = this.pod.children.length;

    this.toggleFilters({ showFilters: true, showSort: true });
    this.sidebar.renderFacets(facets);
    this.#updatePagination(response, { visibleTotal, renderedOnPage });
    this.updateTotalResults(visibleTotal);
    this.sidebar.updateResultCount(visibleTotal);

    this.pod.setAttribute('data-cnstrc-result-id', resultId);
    this.pod.setAttribute('data-cnstrc-num-results', numResults);

    this.#callRecommendations(areAllValidProducts ? numResults : 0, () => {
      window.recommendations?.enqueue();
    });

    this.#trackResultsLoaded(results, resultId, numResults);
    this.#observeImpressions(response);
  }

  /**
   * Fire Constructor results-loaded and GTM windsor_view_search_results events.
   * @param {Array} results - Constructor result items
   * @param {string} resultId - Constructor result ID
   * @param {number} numResults - Total number of results
   */
  #trackResultsLoaded(results, resultId, numResults) {
    const term = new URLSearchParams(window.location.search).get('q') || '';
    const items = mapResultsToItems(results);

    // Constructor tracker: results loaded
    if (this.isSearch) {
      trackSearchResultsLoaded(term, {
        items,
        resultCount: numResults,
        resultPage: this.page,
        resultId,
        selectedFilters: this.activeFilters,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
      });
    } else if (this.isBrowse) {
      trackBrowseResultsLoaded({
        filterName: 'group_id',
        filterValue: this.collectionId,
        items,
        resultCount: numResults,
        resultPage: this.page,
        resultId,
        selectedFilters: this.activeFilters,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
      });
    }

    // GTM: windsor_view_search_results (fire once per search session)
    if (this.isSearch && !this.#searchResultsTracked) {
      this.#searchResultsTracked = true;
      window.dataLayer = window.dataLayer || [];
      viewSearchResultsTracking(
        results.map((r) => ({ ...r.data, variations: r.variations })),
        term,
      );
    }
  }

  /**
   * Observe product card impressions via IntersectionObserver and push to GTM dataLayer.
   * @param {Object} response - Full Constructor API response
   */
  #observeImpressions(response) {
    // Disconnect any existing observer from a previous populate
    if (this.#impressionObserver) {
      this.#impressionObserver.disconnect();
      this.#impressionObserver = null;
    }

    const cards = this.pod.querySelectorAll('.product-card-c-basis, .promo-tile-c-basis');
    if (!cards.length) return;

    const listName =
      response?.response?.groups?.find((group) => group.group_id === this.collectionId)?.display_name ||
      new URLSearchParams(window.location.search).get('q') ||
      '';

    let batch = [];
    let flushTimer = null;

    const flush = () => {
      if (!batch.length) return;
      window.dataLayer = window.dataLayer || [];
      impressionsTracking([...batch], this.#entrySource);
      batch = [];
    };

    this.#impressionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const card = entry.target;
          this.#impressionObserver.unobserve(card);

          const cardIndex = Array.from(cards).indexOf(card);
          const position = cardIndex + (this.page - 1) * this.numberOfResultsPerPage + 1;

          batch.push({
            id: card.getAttribute('data-cnstrc-item-id') || card.getAttribute('data-product-id') || '',
            style: card.getAttribute('data-style') || '',
            name: card.getAttribute('data-cnstrc-item-name') || '',
            price: card.getAttribute('data-cnstrc-item-price') || '',
            originalPrice: card.getAttribute('data-compare-at-price') || '',
            list: listName,
            subcategory: card.getAttribute('data-product-type') || '',
            newArrival: card.getAttribute('data-new-arrival') === 'true',
            isMarkedDown: card.getAttribute('data-is-marked-down') === 'true',
            hasReviews: card.getAttribute('data-has-reviews') === 'true',
            reviewCount: Number(card.getAttribute('data-review-count')) || 0,
            reviewAverage: Number(card.getAttribute('data-review-average')) || 0,
            showsQuadpay: card.getAttribute('data-shows-quadpay') === 'true',
            almostGone: card.getAttribute('data-almost-gone') === 'true',
            position,
          });

          clearTimeout(flushTimer);
          flushTimer = setTimeout(flush, 500);
        });
      },
      { threshold: 0.1 },
    );

    cards.forEach((card) => this.#impressionObserver.observe(card));
  }

  /**
   * Show or hide filter and sort UI elements based on whether there are results to display.
   * @param {Object} param0 - object containing flags for showing filters and sort options
   * @param {Boolean} param0.showFilters - whether to show or hide filter UI elements
   * @param {Boolean} param0.showSort - whether to show or hide sort UI elements
   * @param {Boolean} param0.showResults - it will end up showing results but not filters and sort UI elements.
   * @returns {void} - nothing
   */
  toggleFilters({ showFilters, showSort, showResults }) {
    const mobileFilters = document.querySelector('.plp-c-mobile-filters__pod');
    const mobileSort = document.querySelector('.plp-c-results-header__pod.plp-c-results-header__pod--mobile');
    const desktopFilterSort = document.querySelector('.plp-c-results-header__pod.plp-c-results-header__pod--desktop');
    mobileSort && !mobileSort.hasAttribute('data-show') && mobileSort.setAttribute('data-show', 'true');
    desktopFilterSort &&
      !desktopFilterSort.hasAttribute('data-show') &&
      desktopFilterSort.setAttribute('data-show', 'true');

    showFilters &&
      mobileFilters &&
      !mobileFilters.hasAttribute('data-show') &&
      mobileFilters.setAttribute('data-show', 'true');

    // Browse call exception, in case there are no results.
    !showSort && mobileSort.setAttribute('data-hide', 'sort-filters');
    !showSort && !showFilters && desktopFilterSort.setAttribute('data-hide', 'sort-filters');

    // Collapse empty navigation bars when both filters and sort are hidden
    if (!showSort && !showFilters && !showResults) {
      desktopFilterSort.setAttribute('data-empty', '');
      mobileSort.setAttribute('data-empty', '');
    }
  }

  #call() {
    const params = {
      resultsPerPage: this.numberOfResultsPerPage,
    };

    this.page = parseInt(new URLSearchParams(window.location.search).get('page')) || 1;
    params.page = this.page;
    params.sortBy = this.sortBy;
    params.sortOrder = this.sortOrder;
    params.hiddenFields = ['promo_size', 'alt'];
    params.hiddenFacets = ['promo_size', 'alt'];

    // Check if country is not US, then add country-specific price fields to hidden fields and set suitable filters
    const country = window.Shopify?.country || 'US';
    const isIntl = country !== 'US';
    constructorParamsBuilder(params);
    if (isIntl) {
      params.hiddenFacets = [...(params.hiddenFacets || []), `price${country}`];
    }

    params.filters = { ...params.filters, ...this.activeFilters };

    if (this.isBrowse) {
      return cnstrcSdk.browse.getBrowseResults('group_id', this.collectionId, params);
    }

    // Extract specifically encoded search term from URL without including other existing query params
    const match = window.location.search.match(/[?&]q=(.*?)(?=&[^&]+=|$)/);
    const term = (match && decodeURIComponent(match[1])) || '';
    return cnstrcSdk.search.getSearchResults(term, params);
  }

  #sortOptionHandle(e) {
    const option = e.target;
    this.sortBy = option.dataset.value;
    this.sortOrder = option.dataset.order;
    this.userChangedSort = true;

    this.#restoreSortFromUrl();
    this.#updateUrlAddress(1); // Reset pagination

    // Interaction blockers: block interaction during sort change
    interactionBlocker(true);
    this.#call()
      .then(this.#populate.bind(this))
      .catch((error) => this.#errorHandler(error))
      .finally(() => this.#endCall());

    const sortByValue = SORT_API_TO_URL[`${this.sortBy}:${this.sortOrder}`];
    this.#scrollToTheTop();

    // Defer no user-visible tasks
    setTimeout(() => {
      if (sortByValue) sortSelectionTracking(sortByValue);
    }, 0);
  }

  /**
   * Build and render a promo tile based on product data.
   * @param {Object} product - promo tile slot information
   */
  #buildPromoTile(product) {
    const d = product.data || {};
    const mediaArr = Array.isArray(d.media) ? d.media : [];
    const mediaVideo = mediaArr.find((m) => isVideo(m).result) || '';
    const mediaImage = mediaArr.find((m) => !isVideo(m).result) || '';
    const imageUrl = d.image_url || mediaImage || '';
    const videoUrl = d.video_url || d.original_video_source || mediaVideo || '';
    const promoSize = parseInt(d.promo_size, 10) || 1;

    if (!this.#pivotPromoTile) {
      new PromoTileBuilder()
        .makeExtraBaseClass('plp-c-results')
        .makeSize(promoSize)
        .makeNode(this.pod)
        .setConstructorAttributes(product)
        .makeImage(imageUrl, d.alt || '')
        .makeVideo(videoUrl)
        .makeDestinationUrl(d.url || '')
        .build();

      if (promoSize > 1) {
        this.#pivotPromoTile = true;
      }
      return true;
    } else {
      this.#pivotPromoTile = false;
      return false;
    }
  }

  #buildProductCard(product, { isRecommendation = false, podDisplayName = '' } = {}) {
    const card = new ProductCardBuilder(product.value, product.data?.product_type)
      .makeNode(this.pod)
      .makeExtraBaseClass('plp-c-results')
      .makeLocation(this.isBrowse ? 'browse' : 'search')
      .makeUrl(product.data.url)
      .makeMedia(product.data.media)
      .makeVariants(product.variations)
      .makeProductId(product.data.id)
      .makeVariantId()
      .makePlaceholderTransform()
      .selectColor()
      .makePrices()
      .makeOptions()
      .makeReviews(product.data?.reviews_count, product.data?.reviews_average)
      .makeQuickAdd()
      .makeCardImageGallery()
      .makeBadge(product.data.selected_badge)
      .makeBadgeVisible()
      .makeVideo(product.data.original_video_source)
      .makePromoMessages({
        messages: document.querySelector('[data-plp-promo-messages]'),
        tags: product.data?.tags,
        date: this.date,
      })
      .makeMediaSwitching()
      .setConstructorAttributes(product)
      .setTrackingAttributes(product);

    if (isRecommendation) {
      card.makeConstructorRecommendation(product);
    } else {
      card.setPodId(podDisplayName);
    }

    card.makeSwatches().build();
  }

  async #fetchAndRenderFallbackRecommendations() {
    try {
      const podId = this.fallbackSearchResultsPodId;
      if (!podId || !cnstrcSdk) return;

      const params = { numResults: this.numberOfResultsPerPage };
      constructorParamsBuilder(params, true);

      const response = await cnstrcSdk.recommendations.getRecommendations(podId, params);
      const results = response?.response?.results || [];
      const resultId = response?.result_id || '';

      const searchHeading = document.querySelector('#searchHeading [data-first-heading]');
      const searchResultsHeadings = JSON.parse(
        document.querySelector('script[data-search-state-results-headings]').textContent,
      );
      searchHeading && (searchHeading.textContent = searchResultsHeadings['no-results-heading']);

      // Show "Trending Products" subheading as sibling after the search heading pod
      const searchNoResultsSubheading = document.createElement('p');
      searchNoResultsSubheading.classList.add('plp-c-results__search-subheading');
      searchNoResultsSubheading.textContent = searchResultsHeadings['no-results-subheading'];
      this.pod.before(searchNoResultsSubheading);

      // Clear previous results now that new ones are ready
      this.pod.innerHTML = '';

      results.forEach((product) => {
        this.#buildProductCard(product, { isRecommendation: true });
      });

      // Set Constructor attributes on pod parent
      this.pod.setAttribute('data-cnstrc-result-id', resultId);
      this.pod.setAttribute('data-cnstrc-num-results', results.length);
      this.pod.setAttribute('data-cnstrc-recommendations', '');
      this.pod.setAttribute('data-cnstrc-recommendations-pod-id', this.fallbackSearchResultsPodId || 'search-none-2');

      this.pagination.classList.add('plp-c-pagination__wrapper--hidden');
    } catch (error) {
      console.warn('Constructor PLP: fallback recommendations failed', error);
    }
  }

  // Render a fallback Recommendation section when collections and search brings either results or zero results
  #callRecommendations(numResults, callback) {
    // Guard condition, exit safely whether it brings results
    if (numResults > 0) {
      return callback();
    }

    // Make sure to not choose a wrong rec pod id, because we're pushing fallback rec. pod faster and it is being set as a Constructor rec. pod.
    const constructorRec = document.querySelector('[data-cnstrc-recommendations][data-recommendations]');
    // Guard condition, exit safely whether no recommendation container found or already switched to secondary pod id
    if (!constructorRec || constructorRec.dataset.secondaryPodIdUsed === 'true') {
      return callback();
    }

    // Browse call exception, in case there are no results.
    if (this.isBrowse) {
      return callback();
    }

    const secondaryPodId = document.querySelector('script[data-secondary-pod-id]').dataset.secondaryPodId;

    constructorRec.setAttribute('data-cnstrc-recommendations-pod-id', secondaryPodId);
    constructorRec.setAttribute('data-secondary-pod-id-used', 'true');

    const constructorRecTitle = document.querySelector('h2.product-rec-c-heading');
    const secondaryHeadingText = document.querySelector('script[data-secondary-heading-text]').dataset
      .secondaryHeadingText;
    constructorRecTitle.textContent = secondaryHeadingText;
    return callback();
  }

  /**
   * Hide cached collection that was rendered from server
   * @returns {void} - nothing
   */
  #cleanCachedCollection() {
    const cachedContent = document.querySelector('[data-template-cache]');
    if (!cachedContent) return;

    cachedContent.style.display = 'none';
  }

  /**
   * Handle errors from Constructor API calls.
   * @param {Error} error - error object from API call failure
   */
  #errorHandler(error) {
    const message = 'Constructor Error for PLP';
    console.warn(message, error);
    this.pod.innerHTML = '';
    const cache = document.querySelector('[data-template-cache]');
    if (cache) cache.style.display = '';
  }
}

export default PLP;
